import { ActivityIndicator, Image, TouchableOpacity, View } from 'react-native';
import { AuthStateEnum, MainContext, api } from '../../../App.context';
import Carousel, { Pagination } from 'react-native-snap-carousel';
import { Icon, Text } from 'react-native-elements';
import React, { useEffect, useState } from 'react';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { AccountTile } from '../accounts/components/account-tile.component';
import { CheckListItem } from '../../common/ui/elements/check-list-item.element';
import { LinearGradient } from 'expo-linear-gradient';
import PropTypes from 'prop-types';
import { ScrollableScreenLayout } from '../../common/ui/components/screen-layout.component';
import { TextGradient } from '../../common/ui/elements/text-gradient.element';
import i18n from 'i18n-js';
import theme from '../../common/ui/theme';
import { useToast } from 'react-native-toast-notifications';
import { useWindowDimensions } from 'react-native';

export const HomeScreen = () => {
    const [accounts, setAccounts] = useState([]);
    const [activeAccount, setActiveAccount] = useState();
    const toast = useToast();
    const navigation = useNavigation();

    const fetchAccounts = () => {
        setAccounts(null);
        api.get('/accounts')
            .then((response) => setAccounts(response.accounts))
            .catch((err) => {
                setAccounts([]);
                toast.show(err.message, {
                    type: 'dangerWithTitle',
                    data: {
                        title: i18n.t('common.errorTitle'),
                    },
                });
            });
    };

    useFocusEffect(
        React.useCallback(() => {
            fetchAccounts();
        }, [])
    );

    return (
        <MainContext.Consumer>
            {({ profile }) => (
                <ScrollableScreenLayout
                    fetchAccounts={fetchAccounts}
                    refresh={true}
                >
                    <View
                        style={{
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            alignSelf: 'flex-start',
                            marginBottom: 30,
                        }}
                    >
                        <TextGradient
                            style={{
                                fontSize: 32,
                                fontFamily: 'DMSans_400Regular',
                                fontWeight: '400',
                                lineHeight: 37,
                                color: theme.colors.phthaloBlue,
                            }}
                        >
                            {i18n.t('pages.home.welcome') + ', '}
                            {profile && profile.first_name}
                        </TextGradient>
                    </View>
                    <VerifyIdentity />
                    <Text h4>{i18n.t('pages.home.accountsTitle')}</Text>
                    <AccountsCarousel
                        accounts={accounts}
                        onChange={(account) => setActiveAccount(account)}
                    />
                    <View
                        style={{
                            flexDirection: 'row',
                            justifyContent: 'space-evenly',
                        }}
                    >
                        <TouchableOpacity
                            style={{
                                marginTop: 15,
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                            onPress={() => navigation.push('Home.OpenAccount')}
                        >
                            <Icon
                                name="add"
                                type="ionicon"
                                color={theme.colors.phthaloBlue}
                            />
                            <Text
                                style={{
                                    color: theme.colors.phthaloBlue,
                                    fontFamily: 'DMSans_500Medium',
                                    lineHeight: 26,
                                    letterSpacing: 0.46,
                                    marginLeft: 5,
                                    fontSize: 15,
                                    ...theme.typography.mediumFontWeight,
                                }}
                            >
                                {i18n.t('pages.home.openAccount')}
                            </Text>
                        </TouchableOpacity>
                    </View>
                    <NewsSection accounts={accounts} account={activeAccount} />
                </ScrollableScreenLayout>
            )}
        </MainContext.Consumer>
    );
};

const AccountsCarousel = ({ accounts, onChange }) => {
    const { width } = useWindowDimensions();
    const navigation = useNavigation();
    const [activeIndex, setActiveIndex] = useState(0);

    const selectAccount = (index) => {
        if (accounts !== null) {
            setActiveIndex(index);
            onChange(accounts[index]);
        }
    };

    const viewAccountSummary = (account) => {
        navigation.push('Home.AccountSummary', { accounts, account });
    };

    useEffect(() => selectAccount(0), [accounts]);

    if (accounts === null) {
        return (
            <View
                style={{
                    height: 180,
                    justifyContent: 'center',
                }}
            >
                <ActivityIndicator />
            </View>
        );
    }
    return (
        <View>
            <Carousel
                containerCustomStyle={{
                    marginLeft: -25,
                    marginTop: 10,
                    paddingBottom: 20,
                }}
                data={accounts}
                inactiveSlideScale={0.85}
                inactiveSlideOpacity={0.35}
                inactiveSlideShift={10}
                renderItem={(c) => (
                    <AccountTile
                        account={c.item}
                        onPress={() => viewAccountSummary(c.item)}
                    />
                )}
                sliderWidth={width}
                itemWidth={width}
                onSnapToItem={(index) => selectAccount(index)}
            />
            <Pagination
                dotColor={theme.colors.textBody}
                inactiveDotColor={theme.colors.tabBarInactiveTint}
                activeDotIndex={activeIndex}
                dotsLength={accounts.length}
            />
        </View>
    );
};

AccountsCarousel.propTypes = {
    accounts: PropTypes.array,
    onChange: PropTypes.func,
};

const NewsSection = ({ accounts, account }) => {
    const navigation = useNavigation();
    return (
        <View style={{ flex: 1, marginTop: 35 }}>
            <Text h4>{i18n.t('pages.home.news.title')}</Text>
            <TouchableOpacity
                onPress={() =>
                    navigation.push('Home.MakeDeposit', {
                        accounts,
                        account,
                    })
                }
            >
                <LinearGradient
                    {...theme.gradients.lightNewBlue}
                    style={{
                        borderRadius: 10,
                        minHeight: 157,
                        boxShadow:
                            '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
                    }}
                >
                    <View
                        style={{
                            flex: 1,
                            paddingTop: 23,
                            paddingLeft: 20,
                            paddingRight: 30,
                            paddingBottom: 20,
                            flexDirection: 'row',
                        }}
                    >
                        <View style={{ flex: 1 }}>
                            <Text
                                style={{
                                    fontSize: 17,
                                    fontFamily: 'DMSans_700Bold',
                                    lineHeight: 20,
                                    color: theme.colors.textPrimaryHeading,
                                }}
                            >
                                {i18n.t('pages.home.news.makeDeposit')}
                            </Text>
                            <View style={{ marginTop: 8 }}>
                                <CheckListItem
                                    label={i18n.t(
                                        'pages.home.news.linkAccount'
                                    )}
                                    size={12}
                                    color={theme.colors.textPrimaryHeading}
                                />
                                <CheckListItem
                                    label={i18n.t(
                                        'pages.home.news.completeDeposit'
                                    )}
                                    size={12}
                                    color={theme.colors.textPrimaryHeading}
                                />
                                <CheckListItem
                                    label={i18n.t(
                                        'pages.home.news.willTake2min'
                                    )}
                                    size={12}
                                    color={theme.colors.textPrimaryHeading}
                                />
                            </View>
                        </View>
                        <Image
                            source={require('../../../assets/images/mask_group_deposit.png')}
                            style={{
                                width: 94,
                                height: 94,
                                alignSelf: 'center',
                            }}
                        />
                    </View>
                </LinearGradient>
            </TouchableOpacity>
        </View>
    );
};

NewsSection.propTypes = {
    account: PropTypes.shape({
        id: PropTypes.string,
    }),
    accounts: PropTypes.array,
};

const VerifyIdentity = () => {
    const navigation = useNavigation();
    const { authState, profile } = React.useContext(MainContext);
    const shouldRender =
        authState === AuthStateEnum.Customer_Active &&
        profile.kyc_status === 'REVIEW';

    return (
        shouldRender && (
            <View style={{ marginBottom: 30 }}>
                <TouchableOpacity
                    onPress={() => navigation.push('Home.VerifyIdentity')}
                >
                    <View
                        style={{
                            backgroundColor: theme.colors.phthaloBlue,
                            borderRadius: 4,
                            padding: 16,
                            flexDirection: 'row',
                        }}
                    >
                        <View style={{ flex: 1 }}>
                            <View style={{ flexDirection: 'row' }}>
                                <Icon
                                    name="check-circle-outline"
                                    color={theme.colors.shadWhite}
                                    style={{ marginRight: 12 }}
                                />
                                <View style={{ flex: 1 }}>
                                    <Text
                                        style={{
                                            fontSize: 17,
                                            marginBottom: 4,
                                            lineHeight: 24,
                                            letterSpacing: 0.15,
                                            color: theme.colors.textPrimaryHeading,
                                            ...theme.fontFamilySans.mediumFontWeight
                                        }}
                                    >
                                        {i18n.t('pages.kycInReview.verifyIdentity')}
                                    </Text>
                                    <Text
                                        style={{
                                            fontSize: 14,
                                            marginBottom: 8,
                                            lineHeight: 20,
                                            letterSpacing: 0.15,
                                            color: theme.colors.textPrimaryHeading,
                                            ...theme.fontFamilySans.regularFontWeight
                                        }}
                                    >
                                        {i18n.t('pages.kycInReview.subtitle')}
                                    </Text>
                                    <Text
                                        style={{
                                            fontSize: 14,
                                            lineHeight: 20,
                                            letterSpacing: 0.15,
                                            textDecorationLine: 'underline',
                                            color: theme.colors.textPrimaryHeading,
                                            ...theme.fontFamilySans.regularFontWeight
                                        }}
                                    >
                                        {i18n.t('pages.kycInReview.verifyNow')}
                                    </Text>
                                </View>
                            </View>
                        </View>
                    </View>
                </TouchableOpacity>
            </View>
        )
    );
};
